import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { providerApi } from '../../../services/api';
import { canMarkDelivered, canMarkShipped, isStaleCancelError, storeActionError } from '../../../utils/storeFulfilment';
import { canCancelStoreOrder, isPaidOutEarly } from '../../../utils/storePayout';
import { cancellationCompleted, isTerminal } from '../../../utils/orderStatusLabel';

// Fetch a large page so we can sort/paginate the full set on the client (the backend endpoint
// only supports page/size, not sorting by the derived columns). The page is newest-first
// server-side; the CALENDAR does not read it — it has its own range endpoint (design §8).
export const ORDERS_FETCH_SIZE = 500;

/** Which booking actions a row may offer. Moved verbatim from OrdersManagement.js. */
export const canPerformAction = (order, action) => {
  const status = order.status;
  const orderType = order.orderType || 'SERVICE';

  switch (action) {
    // SERVICE only: a STORE order is shipped / delivered below (complete-job answers 400 for it).
    case 'complete':
      return orderType === 'SERVICE' && status === 'CONFIRMED';
    case 'cancel':
      // A store order can be cancelled while unpaid (CREATED) or paid and not shipped (the buyer is
      // refunded in full); the backend refuses later (StoreCheckoutService.cancelAndRefund). A
      // service order just needs to be live.
      return orderType === 'STORE' ? canCancelStoreOrder(order) : !isTerminal(order);
    case 'markShipped':
      return canMarkShipped(order);
    case 'markDelivered':
      return canMarkDelivered(order);
    default:
      return false;
  }
};

/**
 * Everything the orders page knows that is not presentation: the loaded orders and transactions,
 * the provider's private-note summary, the calendar range, the open dialogs, and the mutating
 * actions with their error mapping. Table sorting/paging and filters live in the components.
 */
export default function useProviderOrders(providerData) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notesSummary, setNotesSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState('details');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [chatOrder, setChatOrder] = useState(null);
  // Store fulfilment dialogs: the order being shipped / delivered.
  const [shipOrder, setShipOrder] = useState(null);
  const [deliverOrder, setDeliverOrder] = useState(null);
  const [confirmCancelOrder, setConfirmCancelOrder] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Email CTA deep link (order-creation design §G): /dashboard?tab=orders&orderId=N.
   *
   * The param is cleared once handled so a refresh does not reopen the dialog. An unknown or
   * foreign id falls back to the list SILENTLY — the provider followed a link, not a button, so
   * an error toast would be noise for something they cannot act on.
   */
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (!orderId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const order = await providerApi.getOrder(Number(orderId));
        if (!cancelled && order) setChatOrder(order);
      } catch {
        // Deliberately silent.
      } finally {
        if (!cancelled) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('orderId');
            return next;
          }, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);

  /** { [orderId]: { count, latestBody, latestAt, searchText } }. A failure leaves the page usable: no badges. */
  const refreshNotesSummary = useCallback(async () => {
    try {
      const rows = await providerApi.getOrderNotesSummary();
      const next = {};
      (rows || []).forEach((r) => { next[r.orderRequestId] = r; });
      setNotesSummary(next);
    } catch {
      setNotesSummary({});
    }
  }, []);

  // The calendar's data: every SERVICE booking overlapping the visible range, straight from the
  // server. The list's newest-500 page is NOT a complete calendar (spec §8). `null` = not loaded yet.
  const [calendarOrders, setCalendarOrders] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const lastRange = useRef(null);
  const calendarSeq = useRef(0);

  const loadCalendarRange = useCallback(async (from, to) => {
    lastRange.current = { from, to };
    // Every call gets a sequence number; only the newest one may touch state, on success, on
    // failure, or when it settles, so a slow stale request can neither overwrite fresh data,
    // raise a misleading error, nor hide the spinner while the current request is still running.
    const seq = ++calendarSeq.current;
    const isCurrent = () => seq === calendarSeq.current;
    setCalendarLoading(true);
    try {
      const rows = await providerApi.getProviderCalendar(from.toISOString(), to.toISOString());
      if (isCurrent()) setCalendarOrders(rows || []);
    } catch (err) {
      if (isCurrent()) {
        setCalendarOrders([]);
        setError(err.response?.data?.message || t('orders.calendar.loadFailed'));
      }
    } finally {
      if (isCurrent()) setCalendarLoading(false);
    }
  }, [t]);

  const fetchData = useCallback(async () => {
    try {
      const [ordersData, txData] = await Promise.all([
        providerApi.getProviderOrders(providerData.id, 0, ORDERS_FETCH_SIZE),
        providerApi.getProviderTransactions(providerData.id),
      ]);
      setOrders(ordersData.content || []);
      setTransactions(txData || []);
    } catch (err) {
      console.error('Failed to fetch orders/transactions:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
    refreshNotesSummary();
    // A mutation (complete, cancel, mark paid…) re-reads the list; the calendar must see it too.
    if (lastRange.current) loadCalendarRange(lastRange.current.from, lastRange.current.to);
  }, [providerData.id, refreshNotesSummary, loadCalendarRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const txByOrder = useMemo(() => transactions.reduce((acc, tx) => {
    const key = tx.orderRequestId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tx);
    return acc;
  }, {}), [transactions]);

  const openDetails = (order, tab = 'details') => {
    setSelectedOrder(order);
    setDetailsTab(tab);
    setShowOrderDetails(true);
  };
  const closeDetails = () => setShowOrderDetails(false);

  const handleOrderAction = async (orderId, action) => {
    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      let successMessage;

      switch (action) {
        case 'complete':
          successMessage = t('orders.completedOk');
          break;
        case 'cancel':
          successMessage = t('orders.cancelledOk');
          break;
        default:
          throw new Error('Invalid action');
      }

      const updated = await providerApi.performOrderAction(orderId, action);
      if (action === 'cancel' && !cancellationCompleted(updated)) {
        successMessage = t('orders.cancellationPending');
      }
      setSuccess(successMessage);

      // Refresh orders and transactions
      await fetchData();

      setShowOrderDetails(false);
      setSelectedOrder(null);
    } catch (err) {
      const order = orders.find((o) => o.id === orderId);
      if (order?.orderType === 'STORE') {
        // Refund wording only when money was actually captured; an unpaid CREATED order has none.
        setError(storeActionError(err, t, { refund: action === 'cancel' && order?.status === 'PAID', cancel: action === 'cancel' }));
        // Shipped or closed behind our back: reload so the row stops offering Cancel.
        if (isStaleCancelError(err)) {
          setShowOrderDetails(false);
          setSelectedOrder(null);
          await fetchData();
        }
      } else {
        const code = err.response?.data?.code;
        const typed = {
          SERVICE_ALREADY_STARTED: 'orders.serviceAlreadyStarted',
          SERVICE_NOT_FINISHED: 'orders.serviceNotFinished',
          BOOKING_DISPUTED: 'orders.bookingDisputed',
        }[code];
        setError(typed ? t(typed) : (err.response?.data?.message || t('orders.actionFailed')));
      }
    } finally {
      setActionLoading(false);
    }
  };

  /** Cancel entry point: a store order already paid out needs an explicit confirmation first. */
  const requestCancel = (order) => {
    if (order.orderType === 'STORE' && isPaidOutEarly(order)) {
      setConfirmCancelOrder(order);
      return;
    }
    handleOrderAction(order.id, 'cancel');
  };

  /** OFFLINE only: records the provider received payment. Keeps the dialog open and refreshes it. */
  const handleMarkPaid = async (orderId) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const updated = await providerApi.markOrderPaid(orderId);
      if (updated) setSelectedOrder(updated);
      setSuccess(t('orders.markPaidOk'));
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.code ? t('orders.markPaidFailed') : (err.response?.data?.message || t('orders.markPaidFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  /** A ship / deliver call succeeded: say so, close everything and reload the list. */
  const handleFulfilmentDone = async (message) => {
    setError('');
    setSuccess(message);
    setShipOrder(null);
    setDeliverOrder(null);
    setShowOrderDetails(false);
    setSelectedOrder(null);
    await fetchData();
  };

  return {
    orders, loading, txByOrder, notesSummary, refreshNotesSummary, refresh: fetchData,
    calendarOrders, calendarLoading, loadCalendarRange,
    error, success, setError, setSuccess,
    actionLoading, selectedOrder, showOrderDetails, detailsTab, openDetails, closeDetails,
    chatOrder, setChatOrder, shipOrder, setShipOrder, deliverOrder, setDeliverOrder,
    confirmCancelOrder, setConfirmCancelOrder,
    requestCancel, handleOrderAction, handleMarkPaid, handleFulfilmentDone,
  };
}
